import { Info, AlertTriangle, AlertOctagon, ShieldAlert } from 'lucide-react';

/**
 * Canonical FIRL Severity Ladder pill — L0 / L1 / L2 / L3 per
 * NACIS Patent Appendix 2 §Severity Ladder.
 *
 * Accepts either the canonical L-prefix value (L0..L3) OR the legacy
 * severity string (informational / elevated / high_risk / critical_risk)
 * for graceful migration.
 */
const STYLES = {
  L0: {
    label:    'L0 — Informational',
    short:    'L0',
    icon:     Info,
    className: 'bg-slate-50 text-slate-700 ring-slate-200',
  },
  L1: {
    label:    'L1 — Elevated',
    short:    'L1',
    icon:     AlertTriangle,
    className: 'bg-amber-50 text-amber-800 ring-amber-200',
  },
  L2: {
    label:    'L2 — High Risk',
    short:    'L2',
    icon:     AlertOctagon,
    className: 'bg-orange-50 text-orange-800 ring-orange-200',
  },
  L3: {
    label:    'L3 — Critical Risk',
    short:    'L3',
    icon:     ShieldAlert,
    className: 'bg-rose-50 text-rose-800 ring-rose-200',
  },
};

const LEGACY_MAP = {
  informational: 'L0',
  elevated:      'L1',
  high_risk:     'L2',
  critical_risk: 'L3',
};

function normalize(value) {
  if (!value) return 'L0';
  if (STYLES[value]) return value;
  const upper = String(value).toUpperCase();
  if (STYLES[upper]) return upper;
  return LEGACY_MAP[String(value).toLowerCase()] ?? 'L0';
}

export default function SeverityLadderBadge({ severity, short = false, className = '' }) {
  const key = normalize(severity);
  const style = STYLES[key];
  const Icon = style.icon;

  return (
    <span
      title={style.label}
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset ${style.className} ${className}`}
    >
      <Icon className="h-3 w-3 flex-none" aria-hidden />
      {short ? style.short : style.label}
    </span>
  );
}
